import { ADMIN_ROLES, RoleKey } from "@/utils/enum/Roles";

export const PERMISSIONS = {
  USERS_VIEW: "users.view.index",
  USERS_MANAGE: "users.manage",
  USERS_VIEW_ALL_LEGACY: "user_view.all",
  ROLES_VIEW: "roles.view",
  ROLES_MANAGE: "roles.manage",
  LOCALITIES_MANAGE: "localities.manage",
  MANAGE_ADMINS: "user.manage_admins",
  BUSINESS_RULE_SETTINGS_VIEW: "business_rule_settings.view",
  BUSINESS_RULE_SETTINGS_EDIT: "business_rule_settings.edit",
} as const;

const DONOR_VIEW_PERMISSIONS = [
  "donor_view",
  "donor_view.all",
  "donor_view.visitor",
  "donor_view.collector",
  "donor_view.transport",
  "donor_view.transport_managed",
  "donor_view.transport_supervised",
  "donor_view.inactive_without_geolocation",
] as const;

export const USER_MENU_PERMISSIONS = [
  PERMISSIONS.USERS_VIEW,
  PERMISSIONS.USERS_MANAGE,
  PERMISSIONS.USERS_VIEW_ALL_LEGACY,
] as const;

export const ROLE_MENU_PERMISSIONS = [
  PERMISSIONS.ROLES_VIEW,
  PERMISSIONS.ROLES_MANAGE,
] as const;

export const HIDDEN_PROFILE_PERMISSIONS = new Set<string>([
  PERMISSIONS.USERS_VIEW_ALL_LEGACY,
]);

export type MenuPermissionItem = {
  permission?: string;
  permissions?: string[];
};

export function hasPermission(
  permissions: Array<string> | undefined,
  slug: string
): boolean {
  return Array.isArray(permissions) && permissions.includes(slug);
}

export function hasAnyPermission(
  permissions: Array<string> | undefined,
  slugs: readonly string[]
): boolean {
  return slugs.some((slug) => hasPermission(permissions, slug));
}

export function hasMenuPermission(
  granted: Array<string> | undefined,
  item: MenuPermissionItem
): boolean {
  const required: string[] = [];

  if (typeof item.permission === "string" && item.permission.length > 0) {
    required.push(item.permission);
  }

  if (Array.isArray(item.permissions)) {
    required.push(...item.permissions);
  }

  if (required.length === 0) {
    return true;
  }

  return hasAnyPermission(granted, required);
}

export function canViewDonors(permissions?: Array<string>): boolean {
  // POST /auth returns concrete slugs; GET /auth also adds donor_view.
  return hasAnyPermission(permissions, DONOR_VIEW_PERMISSIONS);
}

export function canViewUsers(permissions?: Array<string>): boolean {
  return hasAnyPermission(permissions, USER_MENU_PERMISSIONS);
}

export function canManageUsers(permissions?: Array<string>): boolean {
  return hasPermission(permissions, PERMISSIONS.USERS_MANAGE);
}

export function canManageAdmins(permissions?: Array<string>): boolean {
  return hasPermission(permissions, PERMISSIONS.MANAGE_ADMINS);
}

export function canViewRoles(permissions?: Array<string>): boolean {
  return hasAnyPermission(permissions, ROLE_MENU_PERMISSIONS);
}

export function canManageRoles(permissions?: Array<string>): boolean {
  return hasPermission(permissions, PERMISSIONS.ROLES_MANAGE);
}

export function canManageLocalities(permissions?: Array<string>): boolean {
  return hasPermission(permissions, PERMISSIONS.LOCALITIES_MANAGE);
}

export function isAdminRoleKey(role?: string | null): boolean {
  return !!role && ADMIN_ROLES.has(role as RoleKey);
}

export function canSubmitUserForm(
  permissions: Array<string> | undefined,
  targetRole?: string | null
): boolean {
  if (isAdminRoleKey(targetRole)) {
    return canManageAdmins(permissions);
  }

  return canManageUsers(permissions);
}

export function filterAssignableRoles(
  roleList: unknown,
  permissions: Array<string> | undefined,
  currentRole?: string | null
): Record<string, string> {
  if (!roleList || typeof roleList !== "object" || Array.isArray(roleList)) {
    return {};
  }

  const source = roleList as Record<string, string>;

  if (canManageAdmins(permissions)) {
    return { ...source };
  }

  const filtered: Record<string, string> = {};

  for (const [key, label] of Object.entries(source)) {
    if (isAdminRoleKey(key) && key !== currentRole) {
      continue;
    }
    filtered[key] = label;
  }

  return filtered;
}

export function filterProfilePermissionCatalog(
  catalog: unknown
): Record<string, Record<string, string>> {
  if (!catalog || typeof catalog !== "object" || Array.isArray(catalog)) {
    return {};
  }

  const result: Record<string, Record<string, string>> = {};

  for (const [section, perms] of Object.entries(
    catalog as Record<string, unknown>
  )) {
    if (!perms || typeof perms !== "object" || Array.isArray(perms)) {
      continue;
    }

    const filtered: Record<string, string> = {};

    for (const [name, label] of Object.entries(
      perms as Record<string, unknown>
    )) {
      if (HIDDEN_PROFILE_PERMISSIONS.has(name)) {
        continue;
      }
      filtered[name] = label == null ? name : String(label);
    }

    if (Object.keys(filtered).length > 0) {
      result[section] = filtered;
    }
  }

  return result;
}

export function collectRolePermissionFlags(
  permissions: unknown
): Record<string, boolean> {
  const flags: Record<string, boolean> = {};

  if (!Array.isArray(permissions)) {
    return flags;
  }

  permissions.forEach((perm) => {
    if (typeof perm === "string" && perm.length > 0) {
      flags[perm] = true;
      return;
    }

    if (!perm || typeof perm !== "object") {
      return;
    }

    const record = perm as { name?: unknown; slug?: unknown };
    const slug =
      typeof record.name === "string"
        ? record.name
        : typeof record.slug === "string"
        ? record.slug
        : "";

    if (slug.length > 0) {
      flags[slug] = true;
    }
  });

  return flags;
}

function visibleCatalogSlugs(catalog: unknown): Set<string> {
  const slugs = new Set<string>();

  for (const perms of Object.values(filterProfilePermissionCatalog(catalog))) {
    Object.keys(perms).forEach((name) => slugs.add(name));
  }

  return slugs;
}

export function mergeHiddenRolePermissions(
  catalog: unknown,
  current: Record<string, boolean>,
  assignedFromGet: Record<string, boolean>
): Record<string, boolean> {
  const visibleSlugs = visibleCatalogSlugs(catalog);
  const merged = { ...current };

  Object.entries(assignedFromGet).forEach(([slug, enabled]) => {
    if (enabled && !visibleSlugs.has(slug)) {
      merged[slug] = true;
    }
  });

  return merged;
}
